import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { BookOpen, CheckCircle2, ArrowRight, Monitor, Users, Award, Globe } from 'lucide-react';
import { SEO, getBreadcrumbSchema } from '../../components/SEO';

const TRAINING_IMAGE = 'https://images.pexels.com/photos/7647951/pexels-photo-7647951.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

export const TrainingServicePage = () => {
  const { t, direction, language } = useLanguage();

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: language === 'ar' ? 'الرئيسية' : 'Home', url: '/' },
    { name: language === 'ar' ? 'التدريب' : 'Professional Training' }
  ]);

  const trainingTypes = [
    {
      icon: Monitor,
      title: 'Online Training',
      description: 'Live virtual sessions and self-paced e-learning modules accessible from anywhere.',
      features: ['Live webinars', 'Self-paced courses', 'Interactive content', 'Progress tracking'],
    },
    {
      icon: Users,
      title: 'On-site Training',
      description: 'In-person workshops and seminars delivered at your location or our training centers.',
      features: ['Hands-on workshops', 'Team exercises', 'Networking opportunities', 'Customized venues'],
    },
    {
      icon: BookOpen,
      title: 'Blended Learning',
      description: 'Combination of online and in-person training for maximum flexibility and impact.',
      features: ['Flexible scheduling', 'Mixed delivery', 'Personalized paths', 'Extended support'],
    },
  ];

  const benefits = [
    'Customized programs aligned with your business objectives',
    'Expert trainers with real-world industry experience',
    'Flexible delivery options: online, on-site, or hybrid',
    'Comprehensive materials and post-training support',
    'Certification upon completion',
    'Measurable outcomes and ROI tracking',
  ];

  const categories = [
    'Leadership & Management',
    'Project Management',
    'Communication Skills',
    'Technical Training',
    'Sales & Marketing',
    'HR & People Management',
    'Finance & Accounting',
    'Digital Skills',
  ];

  return (
    <div className="min-h-screen" data-testid="training-service-page">
      <SEO
        title="Professional Training & Corporate Development Programs"
        titleAr="التدريب المهني وبرامج تطوير الشركات"
        description="WeDo HR training services offer professional development courses in Europe, Middle East, GCC, and Africa. Online, on-site, and hybrid training. Leadership, management, HR, IT, and more."
        descriptionAr="تقدم خدمات التدريب من ويدو دورات تطوير مهني في أوروبا والشرق الأوسط ودول الخليج وأفريقيا. تدريب عبر الإنترنت وحضوري ومختلط. القيادة والإدارة والموارد البشرية وتكنولوجيا المعلومات."
        keywords="corporate training, professional development, training courses, دورات تدريبية, تدريب الشركات, online training Europe, training Middle East"
        canonicalUrl="/services/training"
        structuredData={breadcrumbSchema}
        language={language}
      />
      {/* Hero Section */}
      <section className="bg-white border-b border-[#E5E7EB] py-20">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#111827] mb-6">
                {t('services.training.title')}
              </h1>
              <p className="text-xl text-[#6B7280] mb-8">
                {t('services.training.description')}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/courses">
                  <Button size="lg" className="btn-primary" data-testid="explore-courses-btn">
                    {t('services.training.cta')}
                    <ArrowRight className={`ms-2 h-5 w-5 ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
                  </Button>
                </Link>
                <Link to="/customized-training-request">
                  <Button size="lg" variant="outline" className="btn-secondary">
                    {t('training.requestCustom')}
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <img 
                src={TRAINING_IMAGE} 
                alt="Training services" 
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Training Types Section */}
      <section className="section-padding bg-[#F9FAFB]">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#111827] mb-4">
              Training Delivery Options
            </h2>
            <p className="text-lg text-[#6B7280] max-w-2xl mx-auto">
              Choose the format that works best for your team
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {trainingTypes.map((type, index) => (
              <Card key={index} className="card-hover" data-testid={`training-type-${index}`}>
                <CardContent className="p-6">
                  <div className="w-14 h-14 rounded-xl bg-[#33AADD]/10 flex items-center justify-center mb-5">
                    <type.icon className="h-7 w-7 text-[#33AADD]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#111827] mb-3">{type.title}</h3>
                  <p className="text-[#6B7280] mb-4">{type.description}</p>
                  <ul className="space-y-2">
                    {type.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-[#6B7280]">
                        <CheckCircle2 className="h-4 w-4 text-[#33AADD]" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#111827] mb-6">
                Why Choose Our Training Services?
              </h2>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3" data-testid={`benefit-${index}`}>
                    <CheckCircle2 className="h-5 w-5 text-[#33AADD] flex-shrink-0 mt-0.5" />
                    <span className="text-[#6B7280]">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Card className="card-hover">
                <CardContent className="p-6 text-center">
                  <Award className="h-10 w-10 text-[#33AADD] mx-auto mb-4" />
                  <h3 className="font-semibold text-[#111827] mb-2">Certified Programs</h3>
                  <p className="text-sm text-[#6B7280]">Industry-recognized certifications</p>
                </CardContent>
              </Card>
              <Card className="card-hover">
                <CardContent className="p-6 text-center">
                  <Users className="h-10 w-10 text-[#33AADD] mx-auto mb-4" />
                  <h3 className="font-semibold text-[#111827] mb-2">Expert Trainers</h3>
                  <p className="text-sm text-[#6B7280]">Industry professionals</p>
                </CardContent>
              </Card>
              <Card className="card-hover">
                <CardContent className="p-6 text-center">
                  <Globe className="h-10 w-10 text-[#33AADD] mx-auto mb-4" />
                  <h3 className="font-semibold text-[#111827] mb-2">Bilingual</h3>
                  <p className="text-sm text-[#6B7280]">English & Arabic support</p>
                </CardContent>
              </Card>
              <Card className="card-hover">
                <CardContent className="p-6 text-center">
                  <BookOpen className="h-10 w-10 text-[#33AADD] mx-auto mb-4" />
                  <h3 className="font-semibold text-[#111827] mb-2">Custom Content</h3>
                  <p className="text-sm text-[#6B7280]">Tailored to your needs</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="section-padding bg-[#F9FAFB]">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#111827] mb-4">
              Training Categories
            </h2>
            <p className="text-lg text-[#6B7280]">
              Explore our wide range of training topics
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {categories.map((category, index) => (
              <Link 
                key={index}
                to={`/courses?category=${encodeURIComponent(category)}`}
                className="p-4 bg-white rounded-xl border border-[#E5E7EB] text-center hover:shadow-md hover:border-[#33AADD] transition-all"
                data-testid={`category-${index}`}
              >
                <span className="text-[#111827] font-medium">{category}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#33AADD]">
        <div className="container-custom text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            {t('training.customTraining')}
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            {t('training.customTrainingDesc')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/courses">
              <Button size="lg" variant="secondary" className="bg-white text-[#33AADD] hover:bg-white/90">
                {t('training.viewAll')}
              </Button>
            </Link>
            <Link to="/customized-training-request">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10" data-testid="custom-training-cta">
                {t('training.requestCustom')}
                <ArrowRight className={`ms-2 h-5 w-5 ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TrainingServicePage;
